/**
 * LanguageSelector Component
 *
 * Allows users to select which programming language implementation to view.
 * Supports multiple languages and shows available/unavailable states.
 *
 * @module concept-graph/components
 */

import React from 'react';
import type { ProgrammingLanguage } from '../types';

export interface LanguageSelectorProps {
  /** Currently selected language */
  selectedLanguage: ProgrammingLanguage;

  /** Available languages for this concept */
  availableLanguages: ProgrammingLanguage[];

  /** All possible languages (shows disabled state for unavailable) */
  allLanguages?: ProgrammingLanguage[];

  /** Callback when language changes */
  onLanguageChange: (language: ProgrammingLanguage) => void;

  /** Custom className */
  className?: string;
}

/**
 * Language selector toggle
 */
export function LanguageSelector({
  selectedLanguage,
  availableLanguages,
  allLanguages = ['C', 'JavaScript', 'Python', 'Rust'],
  onLanguageChange,
  className = ''
}: LanguageSelectorProps) {
  return (
    <div className={`language-selector inline-flex rounded-lg border border-slate-200 p-1 bg-white ${className}`}>
      {allLanguages.map(lang => {
        const isAvailable = availableLanguages.includes(lang);
        const isSelected = lang === selectedLanguage;

        return (
          <button
            key={lang}
            onClick={() => isAvailable && onLanguageChange(lang)}
            disabled={!isAvailable}
            className={`
              px-3 py-1.5 text-sm font-medium rounded-md transition-colors
              ${
                isSelected
                  ? 'bg-orange-500 text-white shadow-sm'
                  : isAvailable
                  ? 'text-slate-700 hover:bg-slate-100'
                  : 'text-slate-300 cursor-not-allowed'
              }
            `}
            data-testid={`language-button-${lang.toLowerCase()}`}
          >
            {lang}
          </button>
        );
      })}
    </div>
  );
}
